import { useApolloClient } from '@apollo/client'
import {
  BankingEntitiesPaymentStatusEnum,
  BankingEntitiesTransactionStatusEnum,
  PaymentTransfersNestedDocument,
} from 'generated/resources/graphql'

import type { Payment, PaymentBase, TransferNode } from './types'
import { isPaymentBase } from './types'

/**
 * Mapped transfer that matches the Payment column structure
 * This allows nested table to render transfers using the same columns
 * It extends PaymentBase to be compatible with the nested table system
 */
export type MappedTransferChild = PaymentBase & {
  // Override specific fields to match transfer data
  date: string | null
  dueDate: string | null
  label: string
  status: BankingEntitiesPaymentStatusEnum
  paymentExecutions: Array<{
    __typename?: 'BankingPaymentExecution'
    id: number
    date: unknown
    executedAt?: unknown
    paidAt?: unknown
    status: BankingEntitiesPaymentStatusEnum
    consent?: { __typename?: 'BankingConsent'; id: number; consentUrl: string } | null
    payer?: unknown
    sepaFile?: unknown
    transfersConnection?: { totalCount: number | null | undefined } | null
  }>
  // Mark as transfer and keep original data
  _isTransfer: true
  _transferData: {
    beneficiaryName: string
    accountNumber: string
    transactionStatus: BankingEntitiesTransactionStatusEnum | null
    sourceId: number | null
    sourceType: string | null
    createdAt: string | null
    rejectionReason: string | null
    paymentExecutionId: number | null
  }
  // Parent payment info for history lookup
  _parentPaymentExecutionIds: number[]
  // All execution IDs from the payment (for complete history including discarded executions)
  _allPaymentExecutionIds: number[]
}

interface ChildrenPaginationInfo {
  total: number
  perPage: number
  currentPage: number
  pagesCount: number
  hasMore: boolean
}

interface ChildrenResponse {
  records: MappedTransferChild[]
  type: 'basic' | 'detailed'
  paginationInfo?: ChildrenPaginationInfo
}

const CHILDREN_PER_PAGE = 25

/**
 * Normalize a status value to a valid BankingEntitiesPaymentStatusEnum
 * Returns 'init' as default if the value is not a valid enum value
 */
const normalizePaymentStatus = (
  status: string | null | undefined
): BankingEntitiesPaymentStatusEnum => {
  if (
    status === 'init' ||
    status === 'pending_consent' ||
    status === 'rejected_consent' ||
    status === 'in_progress' ||
    status === 'rejected' ||
    status === 'done' ||
    status === 'paid' ||
    status === 'partially_paid'
  ) {
    return status
  }
  return 'init'
}

/**
 * Get execution IDs that should be queried for transfers:
 * - All PAID/DONE executions (for their successful transfers)
 * - The last execution (regardless of status, for current state)
 */
const getRelevantExecutionIds = (payment: Payment): number[] => {
  if (!isPaymentBase(payment)) return []
  const executions = payment.paymentExecutions ?? []
  if (executions.length === 0) return []

  const lastExecution = executions[executions.length - 1]
  if (!lastExecution) return []

  const paidDoneExecutionIds = executions
    .filter((exec) => exec.status === 'paid' || exec.status === 'done')
    .map((exec) => exec.id)

  // Combine: all paid/done + last execution (avoiding duplicates)
  const allIds = new Set<number>([...paidDoneExecutionIds, lastExecution.id])
  return Array.from(allIds)
}

/**
 * Get ALL execution IDs from a payment (for complete history including discarded executions)
 */
const getAllExecutionIds = (payment: Payment): number[] => {
  if (!isPaymentBase(payment)) return []
  const executions = payment.paymentExecutions ?? []
  return executions.map((exec) => exec.id)
}

/**
 * Calculate total transfer count across relevant executions
 */
export const getPaymentTransfersCount = (payment: Payment): number => {
  if (!isPaymentBase(payment)) return 0
  const executions = payment.paymentExecutions ?? []
  if (executions.length === 0) return 0

  const lastExecution = executions[executions.length - 1]
  if (!lastExecution) return 0
  const lastExecutionId = lastExecution.id

  // Count from paid/done executions + last execution
  const relevantExecutions = executions.filter(
    (exec) => exec.status === 'paid' || exec.status === 'done' || exec.id === lastExecutionId
  )

  // Use Set to avoid counting the same execution twice
  const countedIds = new Set<number>()
  let total = 0

  for (const exec of relevantExecutions) {
    if (!countedIds.has(exec.id)) {
      countedIds.add(exec.id)
      total += exec.transfersConnection?.totalCount ?? 0
    }
  }

  return total
}

/**
 * Check if payment has any transfers to show
 */
export const paymentHasTransfers = (payment: Payment): boolean => {
  return getPaymentTransfersCount(payment) > 0
}

export const usePaymentNestedTransfers = () => {
  const client = useApolloClient()

  const itemsWithChildren = (payment: PaymentBase): boolean => {
    return paymentHasTransfers(payment)
  }

  const childrenCount = ({
    item,
  }: {
    item: PaymentBase
    pagination?: ChildrenPaginationInfo
  }): number => {
    return getPaymentTransfersCount(item)
  }

  const fetchChildren = async ({
    item,
    pagination,
  }: {
    item: PaymentBase
    filters?: unknown
    pagination?: ChildrenPaginationInfo
  }): Promise<ChildrenResponse> => {
    const executionIds = getRelevantExecutionIds(item)

    if (executionIds.length === 0) {
      return {
        records: [],
        type: 'basic',
      }
    }

    const page = pagination ? pagination.currentPage + 1 : 1
    const perPage = CHILDREN_PER_PAGE

    const { data } = await client.query({
      query: PaymentTransfersNestedDocument,
      variables: {
        paymentExecutionIds: executionIds,
        first: perPage,
        after: pagination?.currentPage ? String(pagination.currentPage * perPage) : undefined,
      },
      fetchPolicy: 'network-only',
    })

    const transfers =
      data.banking.transfersConnection.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is NonNullable<TransferNode> => !!node) ?? []

    // Get all execution IDs for complete history
    const allExecutionIds = getAllExecutionIds(item)

    // Map transfers to match Payment column structure
    const mappedTransfers: MappedTransferChild[] = transfers.map((transfer) => ({
      id: transfer.id,
      // Map to payment column fields
      date: null,
      dueDate: null,
      label: transfer.beneficiaryName || transfer.label || '',
      amountCents: transfer.amountCents,
      currency: transfer.currency,
      status: normalizePaymentStatus(transfer.status),
      paymentExecutions: [],
      requester: transfer.requester
        ? {
            id: transfer.requester.id,
            fullName: transfer.requester.access.fullName,
            access: transfer.requester.access,
          }
        : null,
      // Mark as transfer and keep original data
      _isTransfer: true,
      _transferData: {
        beneficiaryName: transfer.beneficiaryName || '',
        accountNumber: transfer.accountNumber || '',
        transactionStatus: transfer.transaction?.status ?? null,
        sourceId: transfer.sourceId ?? null,
        sourceType: transfer.sourceType ?? null,
        createdAt: transfer.createdAt ?? null,
        rejectionReason: transfer.rejectionReason ?? null,
        paymentExecutionId: null,
      },
      // Store relevant execution IDs (for nested table display)
      _parentPaymentExecutionIds: executionIds,
      // Store ALL execution IDs (for complete history including discarded executions)
      _allPaymentExecutionIds: allExecutionIds,
    }))

    const totalCount = data.banking.transfersConnection.totalCount ?? 0
    const hasNextPage = data.banking.transfersConnection.pageInfo?.hasNextPage ?? false

    return {
      records: mappedTransfers,
      type: 'basic',
      paginationInfo: {
        total: totalCount,
        perPage,
        currentPage: page,
        pagesCount: Math.ceil(totalCount / perPage),
        hasMore: hasNextPage,
      },
    }
  }

  return {
    itemsWithChildren,
    childrenCount,
    fetchChildren,
  }
}
